/**
 * The pieces a village plan is made of, as rows for PlanPiecesCard (owner, 24 Sep 2026: the
 * explainer named "downsizer contribution" and "account-based pension" without ever saying what
 * they are).
 *
 * Built from the same `sellHomeDetails` / funding route the engine projected, so a row can never
 * describe a piece the plan does not have. Every `what` line is two short sentences at most: what
 * the piece is, and what the means tests do with it. The vetted glossary carries the depth.
 */
import type { PlanPiece } from '../../components/narrative/PlanPieces.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { capitalReturnedWords } from '../../components/strategy/annuityWords.ts';

export interface RvPlanPiecesInput {
    fundFrom: string;
    rentHome: boolean;
    entryFee: number;
    /** The engine's sellHomeDetails for a sale, or Manual Control's — null for other routes. */
    allocations?: Record<string, unknown> | null;
    clientName?: string;
    partnerName?: string;
    /** Whether the plan's annuity returns nothing on death (nil residual). */
    nilResidualAnnuity?: boolean;
    /** The capital access schedule's period for the annuity, where the page knows it. */
    annuityCapitalAccessPeriodYears?: number | null;
    annuitySexKnown?: boolean | null;
}

const num = (v: unknown): number => {
    const n = parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : 0;
};

export function rvPlanPieces(i: RvPlanPiecesInput): PlanPiece[] {
    const a = i.allocations || {};
    const you = i.clientName || 'you';
    const partner = i.partnerName || 'your partner';
    const pieces: PlanPiece[] = [];

    // How the move is paid for.
    if (i.fundFrom === 'sell_home') {
        const sale = num(a.salePrice);
        pieces.push({
            key: 'sell-home',
            name: 'Sell the family home',
            amount: sale,
            what: 'The home is sold and, after selling costs, the money pays the entry contribution and funds the rest of the plan. Once sold, its value is money Centrelink counts.',
        });
    } else if (i.fundFrom === 'investment_property') {
        pieces.push({
            key: 'sell-ip',
            name: 'Sell an investment property',
            what: 'The property is sold; after selling costs and capital gains tax, the proceeds pay the entry contribution. Its rent stops counting as income.',
            glossaryTerm: 'capital gains tax',
        });
    } else if (i.rentHome) {
        pieces.push({
            key: 'rent-home',
            name: 'Keep the home and rent it out',
            what: 'The entry contribution is paid from savings and the home is let. The rent counts as income, and the home is counted as an asset once the village unit is the main home.',
        });
    } else {
        pieces.push({
            key: 'savings',
            name: 'Pay from savings',
            what: 'The entry contribution comes out of bank accounts, term deposits, super pensions or shares, in that order unless you chose otherwise. Money spent on the village unit is not assessed.',
        });
    }

    if (i.entryFee > 0) {
        pieces.push({
            key: 'entry',
            name: 'Village entry contribution',
            amount: i.entryFee,
            what: 'What the village charges for the right to live there. Most of it comes back when you leave, less the departure fee, so it stays part of the estate while you are there.',
            glossaryTerm: 'entry contribution',
        });
    }

    // Where the rest of the money goes.
    const dz = num(a.downsizerContribution);
    const pdz = num(a.partnerDownsizerContribution);
    if (dz > 0) {
        pieces.push({
            key: 'downsizer-client',
            name: `Downsizer contribution into super (${you})`,
            amount: dz,
            what: 'A special rule lets each person aged 55 or over put up to $300,000 from selling their home into super, on top of the usual limits. Inside super the earnings are taxed at no more than 15%.',
            glossaryTerm: 'downsizer contribution',
        });
    }
    if (pdz > 0) {
        pieces.push({
            key: 'downsizer-partner',
            name: `Downsizer contribution into super (${partner})`,
            amount: pdz,
            what: 'The same rule, used for the second member of the couple. Super in accumulation is not counted by Centrelink while its owner is under Age Pension age.',
            glossaryTerm: 'downsizer contribution',
        });
    }
    if (a.commenceIncomeStream === true && dz + pdz > 0) {
        pieces.push({
            key: 'abp',
            name: 'Account-based pension',
            what: 'The super is switched on as a regular income you draw from, and its earnings become tax free. The balance stays yours: it is counted as an asset and deemed to earn income.',
            glossaryTerm: 'account-based pension',
        });
    }
    const annuity = num(a.purchaseAnnuity);
    if (annuity > 0) {
        const residual = a.rvAnnuityResidualPercent != null ? num(a.rvAnnuityResidualPercent) : (i.nilResidualAnnuity ? 0 : 0);
        const returned = residual >= 50;
        pieces.push({
            key: 'annuity',
            name: 'Lifetime annuity',
            amount: annuity,
            what: `A lump sum handed to an insurer in return for a guaranteed income for life. Only 60% of it counts in the assets test. ${returned ? capitalReturnedWords({ residualPct: residual, periodYears: i.annuityCapitalAccessPeriodYears ?? null, sexKnown: i.annuitySexKnown ?? null, ownerName: you }) : 'Nothing of the purchase price comes back on death.'}`,
            glossaryTerm: 'lifetime annuity',
        });
    }
    const shares = num(a.purchaseShares);
    if (shares > 0) {
        pieces.push({
            key: 'shares',
            name: 'Shares / managed funds',
            amount: shares,
            what: 'Invested in a spread of shares and funds you can sell at any time. Counted in full as an asset, and deemed to earn income whatever it actually pays.',
            glossaryTerm: 'shares and managed funds',
        });
    }
    const ip = num(a.purchaseInvestmentProperty);
    if (ip > 0) {
        const duty = num(a.investmentPropertyStampDuty);
        pieces.push({
            key: 'ip',
            name: 'Investment property',
            amount: ip,
            what: `A property bought to rent out. Counted in full as an asset, and the rent after expenses counts as income. ${duty > 0 ? `Stamp duty of ${formatCurrency(duty)} is paid on the purchase.` : 'Stamp duty is not modelled because no state was chosen.'}`,
            glossaryTerm: 'investment property',
        });
    }
    const gift = num(a.giftingAmount);
    if (gift > 0) {
        pieces.push({
            key: 'gift',
            name: 'Gift to family',
            amount: gift,
            what: 'Money given away. Up to $10,000 a year ($30,000 over five years) leaves the means tests straight away; anything above that is still counted for five years.',
            glossaryTerm: 'gifting',
        });
    }
    const bond = num(a.funeralBondPurchase);
    if (bond > 0) {
        pieces.push({
            key: 'bond',
            name: 'Funeral bond',
            amount: bond,
            what: 'Money set aside for a funeral with a friendly society or insurer. Within the limit it is exempt from the assets test, and it can only be used for the funeral.',
            glossaryTerm: 'funeral bond',
        });
    }
    return pieces;
}
